import { createSlice } from '@reduxjs/toolkit';
import { PermissionState } from '../types/permissionTypes';
import {
  fetchPermissions,
  fetchPermissionById,
  createPermission,
  updatePermission,
  searchPermissions,
} from '../actions/permissionActions';

const initialState: PermissionState = {
  permissions: [],
  currentPermission: null,
  loading: true, // Set initial loading to true
  error: null,
  pagination: null,
  searchResults: [],
  searching: false,
};

const permissionSlice = createSlice({
  name: 'permissions',
  initialState,
  reducers: {
    clearPermissionError: state => {
      state.error = null;
    },
    clearCurrentPermission: state => {
      state.currentPermission = null;
    },
    clearSearchResults: state => {
      state.searchResults = [];
    },
  },
  extraReducers: builder => {
    builder
      // Fetch Permissions
      .addCase(fetchPermissions.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPermissions.fulfilled, (state, action) => {
        state.loading = false;
        state.permissions = action.payload.data || [];
        state.pagination = action.payload.pagination || null;
      })
      .addCase(fetchPermissions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.permissions = []; // Reset permissions on error
      })

      // Search Permissions
      .addCase(searchPermissions.pending, state => {
        state.searching = true;
        state.error = null;
      })
      .addCase(searchPermissions.fulfilled, (state, action) => {
        state.searching = false;
        state.searchResults = action.payload.data;
      })
      .addCase(searchPermissions.rejected, (state, action) => {
        state.searching = false;
        state.error = action.payload as string;
        state.searchResults = []; // Reset search results on error
      })

      // Fetch Permission by ID
      .addCase(fetchPermissionById.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPermissionById.fulfilled, (state, action) => {
        state.loading = false;

        // Store a copy of the response data
        const permissionData = { ...action.payload.data };

        // If permissions is an array, ensure all Boolean values are properly set
        if (Array.isArray(permissionData.permissions)) {
          permissionData.permissions = permissionData.permissions.map(perm => {
            const newPerm = { ...perm };

            // Force convert permission fields to Boolean
            // Skip non-permission fields
            Object.keys(newPerm).forEach(key => {
              if (key !== 'id' && key !== 'designationId') {
                // Convert any string/number/null value to proper boolean
                // Using strict equality to true to ensure conversion
                newPerm[key] = newPerm[key] === true;
              }
            });

            return newPerm;
          });
        }

        // Store processed data in state
        state.currentPermission = permissionData;
      })
      .addCase(fetchPermissionById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Create Permission
      .addCase(createPermission.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createPermission.fulfilled, (state, action) => {
        state.loading = false;
        state.permissions.push(action.payload.data);
      })
      .addCase(createPermission.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Update Permission
      .addCase(updatePermission.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updatePermission.fulfilled, (state, action) => {
        state.loading = false;
        const updatedPermission = action.payload.data;
        const index = state.permissions.findIndex(p => p.id === updatedPermission.id);
        if (index !== -1) {
          state.permissions[index] = updatedPermission;
        }
        state.currentPermission = updatedPermission;
      })
      .addCase(updatePermission.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearPermissionError, clearCurrentPermission, clearSearchResults } =
  permissionSlice.actions;
export default permissionSlice.reducer;
